import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Button from 'components/atoms/Button';
import { ANDROID_PLATFORM, IOS_PLATFORM } from '../../constants';
import { translateText } from '../../utils';
import { Field, HelpLabel, InlineField } from '../controls';
import css from './AdvancedSettings.css';
import { sitePropType, validationErrorsPropType } from './propTypes';

const UninstallTrackingSection = ({
    site,
    platformFamilyName,
    apsalarEnabled,
    readOnly,
    configuration,
    translate,
    validationErrors,
    onSitePatch,
    onTestFcmIntegration,
}) => (
    <section
        className={classNames(css.formSection, {
            [css.formSectionBeforeIosDeepLink]: platformFamilyName === IOS_PLATFORM && apsalarEnabled,
        })}
    >
        <h4>
            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.UNINSTALL_TRACKING" />
        </h4>
        {platformFamilyName === ANDROID_PLATFORM ? (
            <>
                <div className={css.fcmRow}>
                    <InlineField
                        label={
                            <HelpLabel
                                id="STATIC.PAGES.APPS_CONFIGURATION.FCM_PROJECT_ID"
                                tooltip="STATIC.PAGES.APPS_CONFIGURATION.FCM_PROJECT_ID_TOOLTIP"
                            />
                        }
                        value={site.fcm_project_id}
                        disabled={readOnly || !!configuration.apps_fcm_project_id_readonly}
                        placeholder={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.PLACEHOLDERS.OPTIONAL')}
                        error={validationErrors.fcm_project_id}
                        onChange={value =>
                            onSitePatch({
                                fcm_project_id: value,
                                fcmIntegrationMessage: null,
                                fcmIntegrationStatus: null,
                            })
                        }
                    />
                    <Button
                        type="secondary"
                        level="level2"
                        disabled={readOnly || !site.fcm_project_id || site.isTestingFcmIntegration}
                        showSpinner={!!site.isTestingFcmIntegration}
                        onClick={onTestFcmIntegration}
                    >
                        <Translate id="STATIC.PAGES.APPS_CONFIGURATION.TEST_CONNECTION" />
                    </Button>
                </div>
                <InlineField
                    label={
                        <HelpLabel
                            id="STATIC.PAGES.APPS_CONFIGURATION.FCM_SERVER_KEY"
                            tooltip="STATIC.PAGES.APPS_CONFIGURATION.FCM_SERVER_KEY_TOOLTIP"
                        />
                    }
                    value={site.fcm_server_key}
                    disabled
                    placeholder={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.PLACEHOLDERS.OPTIONAL')}
                    error={validationErrors.fcm_server_key}
                    onChange={value => onSitePatch({ fcm_server_key: value })}
                />
                {site.fcmIntegrationMessage && (
                    <div className={css.fcmIntegrationFeedback}>
                        <div
                            className={classNames(css.fcmIntegrationMessage, {
                                [css.fcmIntegrationError]: site.fcmIntegrationStatus === false,
                                [css.fcmIntegrationSuccess]: site.fcmIntegrationStatus === true,
                            })}
                        >
                            {site.fcmIntegrationMessage}
                        </div>
                        {site.fcmIntegrationStatus === false && (
                            <a
                                className={css.fcmIntegrationLink}
                                href="https://support.singular.net/hc/en-us/articles/115003669231-Setting-Up-Android-Uninstall-Tracking#http_v1_api"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.LEARN_MORE')}
                            </a>
                        )}
                    </div>
                )}
            </>
        ) : (
            <>
                <div className={css.uninstallCertificateRow}>
                    <span className={css.uninstallCertificateLabel}>
                        <Translate id="STATIC.PAGES.APPS_CONFIGURATION.IOS_PUSH_CERTIFICATE" />
                    </span>
                    {!site._local_ios_push_certificate_file ? (
                        <label
                            className={css.uploadBox}
                            htmlFor={`ios-push-certificate-${site.id || site.site_public_id || site.longname || 'new'}`}
                        >
                            <Translate id="STATIC.PAGES.APPS_CONFIGURATION.DRAG_OR_CLICK_TO_UPLOAD_P12" />
                            <input
                                id={`ios-push-certificate-${site.id || site.site_public_id || site.longname || 'new'}`}
                                className={css.hiddenFileInput}
                                type="file"
                                accept=".p12"
                                disabled={readOnly}
                                onChange={event =>
                                    onSitePatch({
                                        _local_ios_push_certificate_file: event.target.files[0],
                                        ios_push_certificate_p12_key: '',
                                    })
                                }
                            />
                        </label>
                    ) : (
                        <div className={css.certificateFileRow}>
                            <span>{site._local_ios_push_certificate_file.name}</span>
                            {!readOnly && (
                                <button
                                    type="button"
                                    className={css.inlineIconButton}
                                    title={translateText(
                                        translate,
                                        'STATIC.PAGES.APPS_CONFIGURATION.REMOVE_CERTIFICATE'
                                    )}
                                    onClick={() => onSitePatch({ _local_ios_push_certificate_file: undefined })}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    )}
                    {validationErrors._local_ios_push_certificate_file && (
                        <span className={css.fieldError}>{validationErrors._local_ios_push_certificate_file}</span>
                    )}
                </div>
                {site._local_ios_push_certificate_file && (
                    <Field
                        label={<Translate id="STATIC.PAGES.APPS_CONFIGURATION.CERTIFICATE_KEY" />}
                        value={site.ios_push_certificate_p12_key}
                        type="password"
                        placeholder={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.PLACEHOLDERS.OPTIONAL')}
                        disabled={readOnly}
                        error={validationErrors.ios_push_certificate_p12_key}
                        onChange={value => onSitePatch({ ios_push_certificate_p12_key: value })}
                    />
                )}
            </>
        )}
    </section>
);

UninstallTrackingSection.propTypes = {
    apsalarEnabled: PropTypes.bool.isRequired,
    configuration: PropTypes.shape({
        apps_fcm_project_id_readonly: PropTypes.bool,
    }).isRequired,
    onSitePatch: PropTypes.func.isRequired,
    onTestFcmIntegration: PropTypes.func.isRequired,
    platformFamilyName: PropTypes.string.isRequired,
    readOnly: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    translate: PropTypes.func.isRequired,
    validationErrors: validationErrorsPropType.isRequired,
};

export default UninstallTrackingSection;
